import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WompiReportsService, ReportConciliationLogDTO } from '../../../services/wompi-reports.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-conciliation-config',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './conciliation-config.component.html',
  styleUrl: './conciliation-config.component.scss'
})
export class ConciliationConfigComponent implements OnInit {
  // Configuración de emails
  emails: string[] = [];
  newEmail = '';
  isLoadingEmails = false;
  isSavingEmails = false;
  
  // Estado de conciliación
  conciliationEnabled = true;
  isLoadingEnabled = false;
  isSavingEnabled = false;
  
  // Logs de conciliación
  conciliationLogs: ReportConciliationLogDTO[] = [];
  isLoadingLogs = false;

  constructor(
    private wompiReportsService: WompiReportsService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadConfiguration();
    this.loadConciliationLogs();
  }

  loadConfiguration(): void {
    this.isLoadingEmails = true;
    this.isLoadingEnabled = true;

    // Cargar emails
    this.wompiReportsService.getNotificationEmails().subscribe({
      next: (emails) => {
        this.emails = emails;
        this.isLoadingEmails = false;
      },
      error: (error) => {
        console.error('Error cargando emails:', error);
        this.isLoadingEmails = false;
      }
    });

    // Cargar estado habilitado/deshabilitado
    this.wompiReportsService.isConciliationEnabled().subscribe({
      next: (enabled) => {
        this.conciliationEnabled = enabled;
        this.isLoadingEnabled = false;
      },
      error: (error) => {
        console.error('Error cargando estado de conciliación:', error);
        this.isLoadingEnabled = false;
      }
    });
  }

  loadConciliationLogs(): void {
    this.isLoadingLogs = true;
    this.wompiReportsService.getConciliationLogs().subscribe({
      next: (logs) => {
        this.conciliationLogs = logs;
        this.isLoadingLogs = false;
      },
      error: (error) => {
        console.error('Error cargando logs:', error);
        this.isLoadingLogs = false;
      }
    });
  }

  addEmail(): void {
    const email = this.newEmail.trim().toLowerCase();
    
    if (!email) {
      this.toastService.showToast('warning', 'Ingresa un email válido');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.toastService.showToast('error', 'El formato del email no es válido');
      return;
    }

    // Verificar duplicados
    if (this.emails.includes(email)) {
      this.toastService.showToast('warning', 'Este email ya está en la lista');
      return;
    }

    this.emails.push(email);
    this.newEmail = '';
    this.saveEmails();
  }

  removeEmail(index: number): void {
    this.emails.splice(index, 1);
    this.saveEmails();
  }

  saveEmails(): void {
    this.isSavingEmails = true;
    this.wompiReportsService.updateNotificationEmails(this.emails).subscribe({
      next: () => {
        this.isSavingEmails = false;
        this.toastService.showToast('success', 'Emails actualizados correctamente');
      },
      error: (error) => {
        console.error('Error guardando emails:', error);
        this.isSavingEmails = false;
        this.toastService.showToast('error', 'Error al guardar los emails');
      }
    });
  }

  toggleConciliation(): void {
    this.isSavingEnabled = true;
    const newState = !this.conciliationEnabled;
    
    this.wompiReportsService.setConciliationEnabled(newState).subscribe({
      next: () => {
        this.conciliationEnabled = newState;
        this.isSavingEnabled = false;
        this.toastService.showToast('success', 
          newState ? 'Conciliación automática habilitada' : 'Conciliación automática deshabilitada'
        );
      },
      error: (error) => {
        console.error('Error cambiando estado:', error);
        this.isSavingEnabled = false;
        this.toastService.showToast('error', 'Error al cambiar el estado');
      }
    });
  }

  runManualConciliation(): void {
    if (!confirm('¿Estás seguro de ejecutar la conciliación manualmente?')) {
      return;
    }

    this.toastService.showToast('warning', 'Ejecutando conciliación...');
    
    this.wompiReportsService.runConciliation().subscribe({
      next: (response) => {
        this.toastService.showToast('success', response.message);
        this.loadConciliationLogs();
      },
      error: (error) => {
        console.error('Error ejecutando conciliación:', error);
        this.toastService.showToast('error', 'Error al ejecutar la conciliación');
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-CO');
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 1: return 'bg-emerald-100 text-emerald-800';
      case 2: return 'bg-amber-100 text-amber-800';
      case 3: return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 0: return 'En Progreso';
      case 1: return 'Completado';
      case 2: return 'Completado con Errores';
      case 3: return 'Fallido';
      default: return 'Desconocido';
    }
  }
}

